import { useState } from "react";
import {
  Box,
  Button,
  DataTable,
  DataTableEmpty,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  MenuItem,
  Text,
} from "@salestrak/ui";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import { useMediaDownloads } from "#app/lib/hooks/use-media-downloads";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import {
  useIssueMediaColumns,
  useIssueMediaGroups,
  useIssueMediaRows,
} from "./issue-media-columns";
import type { Omittable } from "@salestrak/ui";

type IssueMediaTabProps = Omittable<{
  items: IssueMediaItem[];
  /** The owning issue — keys the table's scroll window. */
  issueId: string;
  /** Prefixes download filenames / the zip name. */
  issueName?: string;
  /** Quick links — jump to the field row on the summary tab… */
  onNavigateToField: (customFieldId: string) => void;
  /** …or to the note on the activity tab. */
  onNavigateToNote: (noteId: string, parentNoteId?: string | null) => void;
}>;

/**
 * Media tab: every photo on the issue (custom fields, note attachments,
 * inline note images) in one table, with per-row source, quick-link
 * navigation to where the photo lives, and single/bulk download. The row,
 * column, and group builders live in `issue-media-columns.tsx`, shared with
 * the attachments widget.
 */
export function IssueMediaTab({
  items,
  issueId,
  issueName,
  onNavigateToField,
  onNavigateToNote,
}: IssueMediaTabProps) {
  const { rows, lightboxMedia } = useIssueMediaRows({ items });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { downloadPhoto, downloadAllPhotos, downloadingAll } =
    useMediaDownloads(issueName);

  const columns = useIssueMediaColumns({
    onOpenLightbox: setLightboxIndex,
    onNavigateToField,
    onNavigateToNote,
  });
  const groups = useIssueMediaGroups(rows, issueId);

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <DataTableToolbar>
        <DataTableToolbarLeft>
          <Text size="sm" variant="muted">
            {items.length} {items.length === 1 ? "item" : "items"}
          </Text>
        </DataTableToolbarLeft>
        <DataTableToolbarActions>
          <Button
            variant="outline"
            size="xs"
            disabled={items.length === 0 || downloadingAll}
            onClick={() => downloadAllPhotos(lightboxMedia, "media")}
          >
            <Icon name="download" size="xs" />
            {downloadingAll ? "Preparing zip..." : "Download all"}
          </Button>
        </DataTableToolbarActions>
      </DataTableToolbar>
      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r, i) => `${r.photoId}-${i}`}
          density="regular"
          stickyHeader
          gutterTop="md"
          paddingX="lg"
          renderRowActions={(row) => (
            <MenuItem
              onClick={() =>
                downloadPhoto(row.photoId, row.index + 1, row.item.fileName)
              }
            >
              <Icon name="download" />
              Download
            </MenuItem>
          )}
          emptyState={
            <DataTableEmpty message="No attachments on this Project" />
          }
        />
      </Box>
      <MediaLightbox
        media={lightboxMedia}
        open={lightboxIndex != null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
        initialIndex={lightboxIndex ?? 0}
        title="Attachments"
        downloadPrefix={issueName}
      />
    </Box>
  );
}
